import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { getAllCompanies, getCompanyByDomain } from '../services/companyService.js';
import MainLayout from '../components/Layout/MainLayout.js';
import { updateMetaTags } from '../utils/metaTags.js';

const MAX_COMPANIES_SHOWN = 6;

const LandingPage = () => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [redirect] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    useEffect(() => {
        const checkDomain = async () => {
            try {
                const hostname = window.location.hostname;
                const isMainDomain = hostname === 'håndværkernet.dk' || 
                                   hostname === 'xn--hndvrkernet-x8aq.dk' ||
                                   hostname === 'localhost';

                if (!isMainDomain) {
                    // Look up company by custom domain
                    const company = await getCompanyByDomain(hostname);
                    if (company && company.domain?.subdomain) {
                        // Redirect to the company's subdomain
                        const redirectUrl = `https://${company.domain.subdomain}.xn--hndvrkernet-x8aq.dk`;
                        window.location.href = redirectUrl;
                        return;
                    }
                }

                // If no custom domain match, load companies for landing page
                const data = await getAllCompanies();
                setCompanies(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        checkDomain();
    }, []);

    useEffect(() => {
        updateMetaTags(
            'HåndværkerNet | Professionelle Hjemmesider til Håndværkere',
            'Få en skræddersyet hjemmeside til din håndværkervirksomhed på 5 minutter. AI-genereret indhold, moderne design og lokalområde-optimering. Start gratis i 3 måneder!',
            'https://haandvaerkernet.dk/images/logo_home_click_highres.png'
        );
    }, []);

    if (redirect) {
        return <Navigate to={redirect} replace />;
    }

    if (loading) return null;
    if (error) return <div className="text-red-500">{error}</div>;

    // Filter companies before rendering
    const displayedCompanies = companies.slice(0, MAX_COMPANIES_SHOWN);

    return (
        <MainLayout>
            {/* Hero section - Mørk */}
            <div className="block relative min-h-[700px] lg:min-h-[750px] bg-brand-dark">
                {/* Background image */}
                <div 
                    className="absolute inset-0 bg-cover bg-center" 
                    style={{
                        backgroundImage: "url('/images/landing/front-hero.jpg')"
                    }}
                >
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-brand-dark/60"></div>
                </div>

                {/* Content */}
                <div className="relative max-w-7xl mx-auto h-full flex items-center px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
                    <div className="w-full grid lg:grid-cols-3 gap-6 items-stretch">
                        {/* Main content box */}
                        <div className="lg:col-span-2 bg-white/95 p-8 sm:p-10 rounded-xl border-4 border-brand-light relative flex flex-col shadow-2xl backdrop-blur-sm">
                            {/* Decorative corners */}
                            <div className="absolute w-5 h-5 -top-1.5 -left-1.5 bg-white rounded-tl-lg"></div>
                            <div className="absolute w-5 h-5 -bottom-1.5 -right-1.5 bg-white rounded-br-lg"></div>
                            
                            {/* FREE TRIAL BADGE */}
                            <div className="inline-block bg-green-500 text-white px-5 py-2.5 rounded-full text-sm font-bold mb-6 animate-pulse shadow-md">
                                ✓ GRATIS I 3 MÅNEDER - Intet kreditkort påkrævet
                            </div>
                            
                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-montserrat text-brand-dark mb-5 leading-tight">
                                Få en professionel håndværker-hjemmeside på få minutter
                            </h1>
                            <p className="text-lg sm:text-xl text-gray-700 mb-8 leading-relaxed">
                                Få AI-genereret indhold, der præcist beskriver din håndværksvirksomhed og dine ydelser. Opret din professionelle hjemmeside i dag – helt gratis de første 3 måneder!
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 mt-auto">
                                <Link 
                                    to="/onboarding"
                                    className="inline-flex items-center justify-center px-10 py-4 border border-transparent rounded-lg shadow-xl text-lg font-montserrat font-bold text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 transition-all duration-200 transform hover:scale-105 hover:shadow-2xl"
                                >
                                    Start GRATIS i 3 måneder
                                </Link>
                            </div>
                        </div>

                        {/* Price box */}
                        <div className="bg-white/95 rounded-xl border-4 border-brand-light relative p-8 sm:p-10 flex flex-col shadow-2xl backdrop-blur-sm">
                            {/* Decorative corners - matching main box */}
                            <div className="absolute w-5 h-5 -top-1.5 -left-1.5 bg-white rounded-tl-lg"></div>
                            <div className="absolute w-5 h-5 -bottom-1.5 -right-1.5 bg-white rounded-br-lg"></div>
                            
                            {/* Header badge */}
                            <div className="inline-block bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-6 shadow-md">
                                🎉 SÆRTILBUD
                            </div>
                            
                            <div className="text-center mb-4">
                                <div className="mb-1">
                                    <span className="text-gray-400 line-through text-lg">499 kr/md</span>
                                </div>
                                <div className="flex justify-center items-end mb-2">
                                    <span className="text-5xl font-bold font-montserrat text-green-600">249</span>
                                    <span className="text-lg text-gray-600 mb-1">&nbsp;kr/md</span>
                                </div>
                                <p className="text-green-600 font-bold text-sm">
                                    🎁 Første 3 mdr. GRATIS
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    = kun 8 kr/dag
                                </p>
                            </div>
                            
                            <ul className="space-y-2 text-sm mb-6">
                                <li className="flex items-start">
                                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>Ingen binding</span>
                                </li>
                                <li className="flex items-start">
                                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>Intet kreditkort krævet</span>
                                </li>
                                <li className="flex items-start">
                                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>AI-genereret indhold</span>
                                </li>
                                <li className="flex items-start">
                                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>Hosting inkluderet</span>
                                </li>
                                <li className="flex items-start">
                                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>Gratis support</span>
                                </li>
                            </ul>
                            
                            <Link 
                                to="/onboarding"
                                className="mt-auto block w-full text-center px-8 py-4 border border-transparent rounded-lg shadow-xl text-base font-bold text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 transition-all duration-200 transform hover:scale-105 hover:shadow-2xl"
                            >
                                Kom i gang nu
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Features section - Hvid */}
            <div className="block bg-white">
                <div className="max-w-7xl mx-auto py-24 px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h2 className="text-2xl sm:text-3xl font-semibold font-montserrat text-gray-900 text-center mb-4 break-words">
                            Derfor skal din håndværkervirksomhed vælge HåndværkerNet
                        </h2>
                        <p className="text-gray-600 mb-12">
                            Byg en stærk online tilstedeværelse med en moderne hjemmeside til din håndværkervirksomhed. <br/ >Få flere kunder og bliv fundet på Google.{' '}
                            <Link to="/funktioner" className="text-brand-dark hover:text-brand-dark/90 font-medium">
                                Se alle funktioner
                            </Link>
                            {' · '}
                            <Link to="/hvorfor-hjemmeside-haandvaerker" className="text-brand-dark hover:text-brand-dark/90 font-medium">
                                Hvorfor vælge en hjemmeside?
                            </Link>
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-brand-light/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold font-montserrat mb-2">Hurtig opsætning</h3>
                            <p className="text-gray-600">Få din hjemmeside online på under 5 minutter med vores AI-drevne løsning</p>
                        </div>
                        <div className="text-center">
                        <div className="w-16 h-16 bg-brand-light/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold font-montserrat mb-2">Nem at administrere</h3>
                            <p className="text-gray-600">Opdater nemt din hjemmeside med vores brugervenlige kontrolpanel</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-brand-light/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold font-montserrat mb-2">Gratis support</h3>
                            <p className="text-gray-600">Få personlig hjælp og vejledning til din hjemmeside - helt gratis</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Brancher section - Grå */}
            <div className="block bg-gray-50">
                <div className="max-w-7xl mx-auto py-24 px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl sm:text-3xl font-semibold font-montserrat text-gray-900 mb-4 break-words">
                            Specialiseret til håndværkerbranchen
                        </h2>
                        <p className="text-xl text-gray-600">
                            Skræddersyet løsning til forskellige håndværksfag
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 justify-center">
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <div className="w-10 h-10 bg-brand-light/20 rounded-lg flex items-center justify-center mb-3">
                                <svg className="w-6 h-6 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                            </div>
                            <h3 className="text-base font-semibold font-montserrat mb-2">Byggebranchen</h3>
                            <ul className="text-sm text-gray-600 space-y-1">
                                <li>• Tømrere</li>
                                <li>• Murere</li>
                                <li>• Malere</li>
                                <li>• Gulvlæggere</li>
                                <li>• Tagdækkere</li>
                                <li>• Vinduespudsere</li>
                            </ul>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <div className="w-10 h-10 bg-brand-light/20 rounded-lg flex items-center justify-center mb-3">
                                <svg className="w-6 h-6 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
                                </svg>
                            </div>
                            <h3 className="text-base font-semibold font-montserrat mb-2">Installationsbranchen</h3>
                            <ul className="text-sm text-gray-600 space-y-1">
                                <li>• VVS-installatører</li>
                                <li>• Elektrikere</li>
                                <li>• Ventilationsmontører</li>
                                <li>• Køleteknikkere</li>
                                <li>• Alarminstallatører</li>
                            </ul>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <div className="w-10 h-10 bg-brand-light/20 rounded-lg flex items-center justify-center mb-3">
                                <svg className="w-6 h-6 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-base font-semibold font-montserrat mb-2">Anlægsbranchen</h3>
                            <ul className="text-sm text-gray-600 space-y-1">
                                <li>• Anlægsgartnere</li>
                                <li>• Entreprenører</li>
                                <li>• Kloakmestre</li>
                                <li>• Brolæggere</li>
                                <li>• Jordentreprenører</li>
                            </ul>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <div className="w-10 h-10 bg-brand-light/20 rounded-lg flex items-center justify-center mb-3">
                                <svg className="w-7 h-7 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <g strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M16 6L8 14" />
                                        <path d="M16 18L8 10" />
                                        <circle cx="6" cy="16" r="2" />
                                        <circle cx="6" cy="8" r="2" />
                                    </g>
                                </svg>
                            </div>
                            <h3 className="text-base font-semibold font-montserrat mb-2">Frisører</h3>
                            <ul className="text-sm text-gray-600 space-y-1">
                                <li>• Damefrisører</li>
                                <li>• Herrefrisører</li>
                                <li>• Barberer</li>
                                <li>• Stylister</li>
                                <li>• Skønhedssaloner</li>
                            </ul>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <div className="w-10 h-10 bg-brand-light/20 rounded-lg flex items-center justify-center mb-3">
                                <svg className="w-6 h-6 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m10 0a2 2 0 104 0m-10 0h10" />
                                </svg>
                            </div>
                            <h3 className="text-base font-semibold font-montserrat mb-2">Autobranchen</h3>
                            <ul className="text-sm text-gray-600 space-y-1">
                                <li>• Automekanikere</li>
                                <li>• Autolakerere</li>
                                <li>• Autoelektrikere</li>
                                <li>• Dækcentre</li>
                                <li>• Karrosseriværksteder</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="block bg-gray-50 pb-16">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid md:grid-cols-4 gap-8">
                        <Link to="/tomrer-hjemmeside" className="text-center p-4 block hover:bg-white rounded-lg transition-colors">
                            <h3 className="text-lg font-semibold mb-2 underline">Tømrer Hjemmeside</h3>
                            <p className="text-gray-600">Specialdesignet løsning til tømrervirksomheder med fokus på projekter og referencer</p>
                        </Link>
                        <Link to="/murer-hjemmeside" className="text-center p-4 block hover:bg-white rounded-lg transition-colors">
                            <h3 className="text-lg font-semibold mb-2 underline">Murer Hjemmeside</h3>
                            <p className="text-gray-600">Professionel præsentation af murerprojekter og renovering med før/efter billeder</p>
                        </Link>
                        <Link to="/vvs-hjemmeside" className="text-center p-4 block hover:bg-white rounded-lg transition-colors">
                            <h3 className="text-lg font-semibold mb-2 underline">VVS Hjemmeside</h3>
                            <p className="text-gray-600">Optimeret til VVS-firmaer med fokus på akutservice og faste serviceaftaler</p>
                        </Link>
                        <Link to="/mekaniker-hjemmeside" className="text-center p-4 block hover:bg-white rounded-lg transition-colors">
                            <h3 className="text-lg font-semibold mb-2 underline">Mekaniker Hjemmeside</h3>
                            <p className="text-gray-600">Specialdesignet løsning til autoværksteder med fokus på services og specialer</p>
                        </Link>
                    </div>
                </div>
            </div>

            {/* Firmaer section - Hvid */}
            {companies.length >= 8 && (
                <div className="block bg-white">
                    <div className="max-w-7xl mx-auto py-24 px-4 sm:px-6 lg:px-8">
                        <h2 className="text-2xl font-semibold font-montserrat text-gray-900 text-center mb-8">
                            Håndværkere som allerede har oprettet en hjemmeside
                        </h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {displayedCompanies.map(company => (
                                <Link 
                                    key={company.cvr}
                                    to={company.domain?.subdomain ? `https://${company.domain.subdomain}.håndværkernet.dk` : `/${company.cvr}`}
                                    target="_blank"
                                    className="block p-6 bg-white shadow rounded-lg hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-center mb-4">
                                        {company.logo ? (
                                            <img 
                                                src={company.logo} 
                                                alt={company.name} 
                                                className="h-12 w-auto mr-4"
                                            />
                                        ) : (
                                            <div className="h-12 w-12 bg-brand-light/20 rounded-full flex items-center justify-center mr-4">
                                                <span className="text-brand-dark font-bold text-xl">
                                                    {company.name.charAt(0)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-2">
                                        <h3 className="text-lg font-semibold font-montserrat text-gray-900">{company.name}</h3>
                                        <p className="text-sm text-gray-500">{company.city}</p>
                                    </div>
                                    <p className="text-gray-600 line-clamp-2">
                                        {company.websiteContent?.processedContent?.subtitle || 
                                        company.websiteContent?.subtitle ||
                                        company.existingWebsite?.title ||
                                        "Professionel håndværker med fokus på kvalitet"}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Website example section */}
            <div className="block bg-white">
                <div className="max-w-7xl mx-auto py-24 px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-3 gap-12 items-start">
                        <div 
                            className="relative cursor-pointer group"
                            onClick={() => setShowPreview(true)}
                        >
                            <img 
                                src="/images/landing/website-example.png"
                                alt="Eksempel på håndværker hjemmeside" 
                                className="rounded-lg shadow-xl w-full transition-transform duration-200 group-hover:scale-[1.02]"
                            />
                            <div className="absolute inset-0 bg-brand-dark/10 rounded-lg group-hover:bg-brand-dark/0 transition-all duration-200"></div>
                            
                            {/* Preview overlay */}
                            {showPreview && (
                                <div 
                                    className="fixed inset-0 bg-gray-900/80 z-50 flex items-center justify-center p-8"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="relative max-w-6xl w-full">
                                        <img 
                                            src="/images/landing/website-example.png"
                                            alt="Eksempel på håndværker hjemmeside"
                                            className="w-full h-auto rounded-lg shadow-2xl"
                                        />
                                        <button 
                                            onClick={() => setShowPreview(false)}
                                            className="absolute top-4 right-4 text-white bg-gray-900/50 p-2 rounded-full hover:bg-gray-900/70 transition-colors"
                                        >
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="md:col-span-2">
                            <h2 className="text-2xl sm:text-3xl font-semibold font-montserrat text-gray-900 mb-6">
                                Professionelt hjemmeside design til håndværkere og små virksomheder
                            </h2>
                            <p className="text-gray-600 mb-4">
                                Med HåndværkerNet får du en moderne, professionel håndværker-hjemmeside, der er designet specifikt til håndværksbranchen. Vores hjemmesider præsenterer dine projekter, services og kompetencer på en måde, der tiltrækker nye kunder og styrker din online tilstedeværelse.
                            </p>
                            <p className="text-gray-600">
                                Alle designs er udviklet med fokus på brugervenlighed, SEO-optimering og høj konvertering, så dine besøgende hurtigt finder de informationer, de søger, og let kan kontakte dig. Din hjemmeside er naturligvis 100% mobilvenlig og ser skarp ud på både computer, tablet og mobil.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Video guide section */}
            <div className="block bg-gray-50 py-24">
                <div className="max-w-3xl mx-auto px-4 text-center">
                    <h2 className="text-2xl sm:text-3xl font-semibold font-montserrat text-gray-900 mb-6">
                        Video guide: Sådan kommer du i gang
                    </h2>
                    <p className="text-gray-600 mb-8">
                        Se en kort video, hvor jeg viser dig hvordan du opretter din egen håndværker-hjemmeside på få minutter.
                    </p>
                    <div className="w-full max-w-2xl mx-auto rounded-lg overflow-hidden shadow-lg" style={{ aspectRatio: "16/10" }}>
                        <iframe
                            src="https://www.youtube.com/embed/JNU9-SdUYFs"
                            title="Video guide: Sådan kommer du i gang"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                        ></iframe>
                    </div>
                </div>
            </div>

            {/* Testimonials / Social Proof Section - NYHED */}
            <div className="block bg-white py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl sm:text-3xl font-semibold font-montserrat text-gray-900 mb-4">
                            Hvad siger håndværkere om HåndværkerNet?
                        </h2>
                        <p className="text-xl text-gray-600">
                            Se hvordan andre håndværkere har fået succes med deres nye hjemmeside
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-8">
                        {/* Testimonial 1 */}
                        <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-lg border border-gray-100">
                            <div className="flex items-center mb-4">
                                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-2xl mr-4">
                                    MJ
                                </div>
                                <div>
                                    <h4 className="font-bold text-lg">Michael Jensen</h4>
                                    <p className="text-sm text-gray-600">Elektriker, København</p>
                                </div>
                            </div>
                            <div className="flex mb-3">
                                {[...Array(5)].map((_, i) => (
                                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                ))}
                            </div>
                            <p className="text-gray-700 italic mb-4">
                                "Jeg havde aldrig tid til at lave en hjemmeside selv, og bureauerne var alt for dyre. Med HåndværkerNet fik jeg en professionel side på 10 minutter. Nu får jeg flere henvendelser hver uge!"
                            </p>
                        </div>

                        {/* Testimonial 2 */}
                        <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-lg border border-gray-100">
                            <div className="flex items-center mb-4">
                                <div className="w-16 h-16 bg-gradient-to-br from-amber-600 to-yellow-700 rounded-full flex items-center justify-center text-white font-bold text-2xl mr-4">
                                    LN
                                </div>
                                <div>
                                    <h4 className="font-bold text-lg">Lars Nielsen</h4>
                                    <p className="text-sm text-gray-600">Tømrer, Aarhus</p>
                                </div>
                            </div>
                            <div className="flex mb-3">
                                {[...Array(5)].map((_, i) => (
                                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                ))}
                            </div>
                            <p className="text-gray-700 italic mb-4">
                                "Jeg var skeptisk i starten, men resultatet er helt fantastisk. Mine kunder kan nu se mine projekter online, og jeg virker meget mere professionel. Bedste investering længe!"
                            </p>
                        </div>

                        {/* Testimonial 3 */}
                        <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-lg border border-gray-100">
                            <div className="flex items-center mb-4">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-2xl mr-4">
                                    TA
                                </div>
                                <div>
                                    <h4 className="font-bold text-lg">Thomas Andersen</h4>
                                    <p className="text-sm text-gray-600">VVS-installatør, Odense</p>
                                </div>
                            </div>
                            <div className="flex mb-3">
                                {[...Array(5)].map((_, i) => (
                                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                ))}
                            </div>
                            <p className="text-gray-700 italic mb-4">
                                "Super simpelt system! Jeg er ikke IT-kyndig, men jeg kunne nemt opdatere min hjemmeside selv. Mine kunder kan nu kontakte mig direkte, og jeg sparer tid hver uge på administration."
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Historie section */}
            <div className={`block ${companies.length >= 4 ? 'bg-gray-50' : 'bg-white'}`}>
                <div className="max-w-7xl mx-auto py-24 px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-3 gap-12 items-start">
                        <div className="md:col-span-2">
                            <h2 className="text-2xl sm:text-3xl font-semibold font-montserrat text-gray-900 mb-6">
                                Historien bag HåndværkerNet
                            </h2>
                            <p className="text-gray-600 mb-4">
                                Inspirationen til HåndværkerNet kom fra min lillebror, som er uddannet murer. 
                                Da han overvejede at starte som selvstændig, stødte han på en velkendt udfordring: 
                                Behovet for en professionel hjemmeside til en overkommelig pris.
                            </p>
                            <p className="text-gray-600">
                                Dette blev startskuddet til at udvikle en løsning, der gør det nemt og 
                                økonomisk tilgængeligt for håndværkere at få en professionel online tilstedeværelse.
                            </p>
                            <Link 
                                to="/om"
                                className="inline-flex items-center mt-6 text-brand-dark hover:text-brand-dark/90"
                            >
                                Læs mere
                                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                </svg>
                            </Link>
                        </div>
                        <div className="relative">
                            <img 
                                src="/images/landing/workshop.png"
                                alt="Professionelt værksted" 
                                className="rounded-lg shadow-xl w-full"
                            />
                            <div className="absolute inset-0 bg-brand-dark/10 rounded-lg"></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pricing section - Ændrer baggrund baseret på om firma sektion vises */}
            <div className={`block ${companies.length >= 4 ? 'bg-white' : 'bg-gray-50'}`}>
                <div className="max-w-7xl mx-auto py-24 px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-block bg-green-500 text-white px-6 py-2 rounded-full text-sm font-bold mb-4">
                            🎉 SÆRTILBUD: 3 MÅNEDER GRATIS
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-semibold font-montserrat text-gray-900 mb-4">
                            Enkel og gennemskuelig prissætning
                        </h2>
                        <p className="text-xl text-gray-600 mb-8">
                            Start gratis og betal kun hvis du er tilfreds
                        </p>
                    </div>
                    <div className="max-w-lg mx-auto bg-white shadow-xl rounded-lg overflow-hidden border-2 border-green-500">
                        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white text-center py-3 font-bold">
                            MEST POPULÆRE
                        </div>
                        <div className="px-6 py-8">
                            <div className="text-center mb-6">
                                <div className="mb-2">
                                    <span className="text-gray-400 line-through text-2xl">499 kr/md</span>
                                </div>
                                <div className="flex justify-center items-end">
                                    <span className="text-5xl font-bold font-montserrat text-green-600">249</span>
                                    <span className="text-xl text-gray-600 mb-2">&nbsp;kr/md</span>
                                </div>
                                <p className="text-center text-green-600 font-bold text-lg mt-2">
                                    🎁 De første 3 måneder er GRATIS
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    Det svarer til kun 8 kr/dag efter prøveperioden
                                </p>
                            </div>
                            <ul className="mt-6 space-y-4">
                                <li className="flex items-center">
                                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="font-semibold">Ingen binding</span> - opsig når som helst
                                </li>
                                <li className="flex items-center">
                                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="font-semibold">Intet kreditkort krævet</span> for gratis periode
                                </li>
                                <li className="flex items-center">
                                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    AI-genereret indhold til hele hjemmesiden
                                </li>
                                <li className="flex items-center">
                                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    Hosting, domæne og support inkluderet
                                </li>
                                <li className="flex items-center">
                                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    Ubegrænset antal sider og billeder
                                </li>
                                <li className="flex items-center">
                                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    Gratis support når du har brug for det
                                </li>
                            </ul>
                            <Link 
                                to="/onboarding"
                                className="mt-8 block w-full text-center px-6 py-4 border border-transparent rounded-md shadow-lg text-lg font-bold text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 transition-all duration-200 transform hover:scale-105"
                            >
                                Start GRATIS i 3 måneder
                            </Link>
                            <p className="text-center text-xs text-gray-500 mt-3">
                                ✓ Opret på under 5 minutter • ✓ Ingen risiko • ✓ Opsig når som helst
                            </p>
                            <Link
                                to="/hvad-er-med-i-prisen"
                                className="block mt-4 text-center text-sm text-brand-dark underline hover:text-brand-dark/80"
                            >
                                Se alle funktioner og fordele
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* FAQ section */}
            <div className="block bg-white py-24">
                <div className="max-w-3xl mx-auto px-4">
                    <h2 className="text-2xl font-semibold text-center mb-8">Ofte stillede spørgsmål</h2>
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-lg font-semibold mb-2">Hvor hurtigt kan jeg få min håndværker-hjemmeside online?</h3>
                            <p className="text-gray-600">Din hjemmeside kan være online inden for 5 minutter med vores AI-drevne løsning.</p>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold mb-2">Hvordan hjælper det min håndværkervirksomhed?</h3>
                            <p className="text-gray-600">En professionel hjemmeside øger din synlighed på Google, giver dig flere lokale kunder og styrker din troværdighed.</p>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold mb-2">Hvad koster en håndværker-hjemmeside?</h3>
                            <p className="text-gray-600">Start gratis i 3 måneder, derefter kun 249 kr/md uden binding.</p>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default LandingPage;